import { motion, AnimatePresence } from "motion/react";
import { CheckCircle, AlertTriangle, X, Info, AlertCircle } from "lucide-react";
import { createContext, useContext, useState, useCallback, ReactNode } from "react";

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
    id: string;
    type: ToastType;
    title: string;
    message?: string;
    duration?: number;
}

interface ToastContextType {
    showToast: (type: ToastType, title: string, message?: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function useToast() {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
}

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const showToast = useCallback((type: ToastType, title: string, message?: string, duration = 4000) => {
        const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newToast: Toast = { id, type, title, message, duration };

        setToasts(prev => [...prev, newToast]);

        if (duration > 0) {
            setTimeout(() => {
                setToasts(prev => prev.filter(t => t.id !== id));
            }, duration);
        }
    }, []);

    const removeToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            <ToastContainer toasts={toasts} onRemove={removeToast} />
        </ToastContext.Provider>
    );
}

function ToastContainer({ toasts, onRemove }: { toasts: Toast[], onRemove: (id: string) => void }) {
    const getIcon = (type: ToastType) => {
        switch (type) {
            case 'success': return CheckCircle;
            case 'error': return AlertCircle;
            case 'warning': return AlertTriangle;
            default: return Info;
        }
    };

    const getColors = (type: ToastType) => {
        switch (type) {
            case 'success': return { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', icon: 'text-emerald-500' };
            case 'error': return { bg: 'bg-red-500/10', border: 'border-red-500/30', icon: 'text-red-500' };
            case 'warning': return { bg: 'bg-amber-500/10', border: 'border-amber-500/30', icon: 'text-amber-500' };
            default: return { bg: 'bg-blue-500/10', border: 'border-blue-500/30', icon: 'text-blue-500' };
        }
    };

    return (
        <div className="fixed bottom-6 right-6 z-100 flex flex-col gap-3 max-w-md">
            <AnimatePresence>
                {toasts.map(toast => {
                    const Icon = getIcon(toast.type);
                    const colors = getColors(toast.type);

                    return (
                        <motion.div
                            key={toast.id}
                            initial={{ opacity: 0, y: 20, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, x: 100, scale: 0.95 }}
                            className={`${colors.bg} ${colors.border} border backdrop-blur-xl rounded-xl p-4 shadow-2xl shadow-black/50 flex items-start gap-3`}
                        >
                            <div className={`w-6 h-6 ${colors.icon} shrink-0`}>
                                <Icon className="w-6 h-6" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-white">{toast.title}</div>
                                {toast.message && (
                                    <div className="text-sm text-neutral-400 mt-0.5">{toast.message}</div>
                                )}
                            </div>
                            <button
                                onClick={() => onRemove(toast.id)}
                                className="text-neutral-500 hover:text-white transition-colors shrink-0"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </motion.div>
                    );
                })}
            </AnimatePresence>
        </div>
    );
}

// Skeleton loading components
export function SkeletonLoader({ className = "", style }: { className?: string; style?: React.CSSProperties }) {
    return (
        <div className={`animate-pulse bg-neutral-800 rounded ${className}`} style={style} />
    );
}

export function CardSkeleton() {
    return (
        <div className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-6 space-y-4">
            <div className="flex items-center gap-4">
                <SkeletonLoader className="w-12 h-12 rounded-xl" />
                <div className="flex-1 space-y-2">
                    <SkeletonLoader className="h-4 w-3/4" />
                    <SkeletonLoader className="h-3 w-1/2" />
                </div>
            </div>
            <SkeletonLoader className="h-20 w-full" />
        </div>
    );
}

export function TableRowSkeleton() {
    return (
        <div className="flex items-center gap-4 p-4 border-b border-neutral-800 last:border-0">
            <SkeletonLoader className="w-10 h-10 rounded-lg" />
            <div className="flex-1 space-y-2">
                <SkeletonLoader className="h-4 w-32" />
                <SkeletonLoader className="h-3 w-24" />
            </div>
            <SkeletonLoader className="h-4 w-20" />
            <SkeletonLoader className="h-4 w-20" />
        </div>
    );
}

export function ChartSkeleton() {
    // Seeded heights for deterministic rendering (React purity compliant)
    const barHeights = [45, 72, 38, 85, 62, 48, 91, 55, 68, 42, 77, 59];

    return (
        <div className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-6">
            <div className="flex justify-between items-center mb-6">
                <SkeletonLoader className="h-6 w-48" />
                <div className="flex gap-2">
                    {[1, 2, 3, 4].map(i => (
                        <SkeletonLoader key={i} className="h-8 w-12 rounded-lg" />
                    ))}
                </div>
            </div>
            <div className="h-64 flex items-end gap-2 justify-around">
                {barHeights.map((height, i) => (
                    <SkeletonLoader
                        key={i}
                        className="flex-1 rounded-t"
                        style={{ height: `${height}%` }}
                    />
                ))}
            </div>
        </div>
    );
}

export function DashboardSkeleton() {
    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div className="space-y-2">
                    <SkeletonLoader className="h-8 w-64" />
                    <SkeletonLoader className="h-4 w-48" />
                </div>
                <SkeletonLoader className="h-10 w-32 rounded-xl" />
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map(i => (
                    <div key={i} className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-6">
                        <SkeletonLoader className="h-4 w-24 mb-2" />
                        <SkeletonLoader className="h-8 w-32" />
                    </div>
                ))}
            </div>

            {/* Chart */}
            <ChartSkeleton />

            {/* Table */}
            <div className="bg-neutral-900/50 border border-neutral-800 rounded-2xl overflow-hidden">
                {[1, 2, 3, 4, 5].map(i => (
                    <TableRowSkeleton key={i} />
                ))}
            </div>
        </div>
    );
}

// Loading spinner
export function LoadingSpinner({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
    const sizeClasses = {
        sm: 'w-4 h-4 border-2',
        md: 'w-8 h-8 border-2',
        lg: 'w-12 h-12 border-3'
    };

    return (
        <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className={`${sizeClasses[size]} border-neutral-700 border-t-emerald-500 rounded-full`}
        />
    );
}

// Full page loading
export function PageLoader() {
    return (
        <div className="fixed inset-0 bg-neutral-950 flex items-center justify-center z-50">
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
            >
                <LoadingSpinner size="lg" />
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="mt-4 text-neutral-400 text-sm"
                >
                    Loading...
                </motion.p>
            </motion.div>
        </div>
    );
}

// Action button with loading state
export function ActionButton({
    children,
    onClick,
    loading = false,
    variant = 'primary',
    disabled = false,
    className = ''
}: {
    children: ReactNode;
    onClick?: () => void;
    loading?: boolean;
    variant?: 'primary' | 'secondary' | 'danger';
    disabled?: boolean;
    className?: string;
}) {
    const baseStyles = "px-6 py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed";

    const variantStyles = {
        primary: "bg-emerald-500 text-black hover:bg-emerald-400",
        secondary: "bg-neutral-800 text-white hover:bg-neutral-700",
        danger: "bg-red-500 text-white hover:bg-red-400"
    };

    return (
        <button
            onClick={onClick}
            disabled={disabled || loading}
            className={`${baseStyles} ${variantStyles[variant]} ${className}`}
        >
            {loading ? (
                <>
                    <LoadingSpinner size="sm" />
                    <span>Processing...</span>
                </>
            ) : children}
        </button>
    );
}
